// src/screens/activeScreen/Active.js
import React, { useEffect, useRef, useState } from "react";
import { EyeOff, LoaderCircle, Pin, Trash2 } from "lucide-react";

import "./active.css";
import { useActive } from "./active.functions";
import { Button } from "../../components/primitives";
import { formatConversationTimestamp } from "../../components/conversation-list/conversation-list.functions";
import { computePaginationRange } from "../../components/conversation-list/pagination.functions";
import { ConfirmActionDialog, ProfileDialog, ReportDialog } from "../../components/dialogs";
import { ProfileAvatar } from "../../components/profile-display";
import { client } from "../../amplify-client";
import { createUserReport } from "../../api/reporting/reporting-service";
import { publishCommunicationEvent } from "../../api/communication";
import { toast } from "../../components/toast";

const PINNED_CHATS_STORAGE_KEY = "activeConversations.pinnedChats.v1";
const ACTIVE_LIST_PAGE_SIZE = 15;

function buildPairChatPk(userA, userB) {
  const [first, second] = [String(userA || ""), String(userB || "")].sort();
  return `CHAT#PAIR#${first}#${second}`;
}

function ActiveInner() {
  const hasHydratedListRef = useRef(false);
  const [activeListMode, setActiveListMode] = useState("chats");
  const [chatsPage, setChatsPage] = useState(1);
  const [requestsPage, setRequestsPage] = useState(1);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [blockActionTargetId, setBlockActionTargetId] = useState("");
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [hideTargetChatId, setHideTargetChatId] = useState("");
  const [hideConfirmSubmitting, setHideConfirmSubmitting] = useState(false);
  const [pinnedConversationIds, setPinnedConversationIds] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(PINNED_CHATS_STORAGE_KEY);
      const parsed = JSON.parse(raw || "[]");
      if (!Array.isArray(parsed)) return [];
      return [...new Set(
        parsed.map((value) => String(value || "").trim()).filter(Boolean)
      )];
    } catch {
      return [];
    }
  });
  const {
    me,
    chatListLoading,
    availableChats,
    acceptingChatRequestIds,
    acceptChatRequest,
    denyChatRequest,
    deleteChatRequest,
    hideConversation,
  } = useActive();

  useEffect(() => {
    if (!chatListLoading) {
      hasHydratedListRef.current = true;
    }
  }, [chatListLoading]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        PINNED_CHATS_STORAGE_KEY,
        JSON.stringify(pinnedConversationIds)
      );
    } catch (error) {
      console.warn("Failed to persist pinned active conversations:", error);
    }
  }, [pinnedConversationIds]);

  useEffect(() => {
    const visibleChatIds = new Set(
      availableChats
        .filter((chat) => chat?.status === "accepted")
        .map((chat) => String(chat?.chatPk || "").trim())
        .filter(Boolean)
    );
    setPinnedConversationIds((previous) => {
      const next = previous.filter((chatPk) => visibleChatIds.has(chatPk));
      return next.length === previous.length ? previous : next;
    });
  }, [availableChats]);

  const showInitialBlockingLoad =
    chatListLoading &&
    !hasHydratedListRef.current &&
    availableChats.length === 0;

  useEffect(() => {
    let cancelled = false;

    async function loadBlockedUsers() {
      try {
        const query = client?.queries?.listBlockedUsers;
        if (typeof query !== "function") return;

        const response = await query({ limit: 200 });
        if (response?.errors?.length) {
          throw new Error(response.errors.map((e) => e.message).join(", "));
        }

        const ids = [...new Set(
          (Array.isArray(response?.data) ? response.data : [])
            .map((entry) => String(entry?.cognitoId || "").trim())
            .filter(Boolean)
        )];

        if (!cancelled) {
          setBlockedUserIds(ids);
        }
      } catch (error) {
        console.error("Failed to load blocked users for active profile modal:", error);
      }
    }

    loadBlockedUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  const goToChat = (_chatPk, title) => {
    // Open the chat in a new tab and leave the original /active dashboard
    // untouched. The URL is just /active/chat/<username> — the chat screen
    // resolves the chatPk by matching the counterpart name in the loaded
    // inbox (chat.functions.js useEnsureSelectedChat).
    //
    // Synthetic anchor click instead of window.open: window.open with
    // "noopener" returns null on success in some browsers, which made the
    // dashboard tab also navigate away. An anchor click is treated as a
    // regular link by the browser and never popup-blocked.
    const slug = encodeURIComponent(title || "chat");
    const anchor = document.createElement("a");
    anchor.href = `/active/chat/${slug}`;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const openProfileFromChat = (chat) => {
    if (!chat) return;
    const profile = chat.counterpartProfile || {
      cognitoId: chat.counterpartId || "",
      profileName: chat.title || "User",
      profilePicture: chat.profilePicture || "",
    };
    setSelectedProfile(profile);
  };

  const closeProfile = () => {
    setSelectedProfile(null);
  };

  const openReportForSelectedProfile = () => {
    const targetId = String(selectedProfile?.cognitoId || "").trim();
    if (!targetId) return;

    setReportTarget({
      targetUserId: targetId,
      targetLabel: selectedProfile?.profileName || selectedProfile?.name || "User",
      sourceSurface: "PROFILE",
    });
    setReportDialogOpen(true);
  };

  const closeReportDialog = () => {
    setReportDialogOpen(false);
    setReportTarget(null);
  };

  const submitUserReport = async ({ reasonCode, reasonText }) => {
    if (!reportTarget?.targetUserId) {
      throw new Error("Report target is missing.");
    }

    await createUserReport({
      targetType: "USER",
      targetUserId: reportTarget.targetUserId,
      reasonCode,
      reasonText,
      sourceSurface: reportTarget.sourceSurface || "PROFILE",
    });
    toast.success("Report submitted. Our admins will review it.");
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    if (Number.isNaN(birthDate.getTime())) return null;
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }
    return age;
  };

  const selectedProfileId = String(selectedProfile?.cognitoId || "").trim();
  const selectedProfileIsBlocked = Boolean(
    selectedProfileId && blockedUserIds.includes(selectedProfileId)
  );

  const toggleBlockForSelectedProfile = async () => {
    if (!selectedProfileId || blockActionTargetId) return;

    const currentlyBlocked = blockedUserIds.includes(selectedProfileId);
    setBlockActionTargetId(selectedProfileId);

    try {
      if (currentlyBlocked) {
        const unblock = client?.mutations?.unblockUser;
        if (typeof unblock !== "function") {
          throw new Error("unblockUser mutation is unavailable. Deploy backend schema updates.");
        }
        const response = await unblock({ blockedUserId: selectedProfileId });
        if (response?.errors?.length) {
          throw new Error(response.errors.map((err) => err.message).join(", "));
        }
        setBlockedUserIds((prev) => prev.filter((id) => id !== selectedProfileId));
        const myUserId = String(me?.userId || "").trim();
        if (myUserId) {
          const chatPk = buildPairChatPk(myUserId, selectedProfileId);
          publishCommunicationEvent({
            type: "chat_visibility_changed",
            chatPk,
            status: "visible",
            activityAt: new Date().toISOString(),
          });
        }
      } else {
        const block = client?.mutations?.blockUser;
        if (typeof block !== "function") {
          throw new Error("blockUser mutation is unavailable. Deploy backend schema updates.");
        }
        const response = await block({ blockedUserId: selectedProfileId });
        if (response?.errors?.length) {
          throw new Error(response.errors.map((err) => err.message).join(", "));
        }
        setBlockedUserIds((prev) =>
          prev.includes(selectedProfileId) ? prev : [...prev, selectedProfileId]
        );
      }
    } catch (error) {
      console.error("Failed to update block status from active profile modal:", error);
      toast.error(error?.message || "Failed to update block settings.");
    } finally {
      setBlockActionTargetId("");
    }
  };

  const openHideConversationConfirm = (chatId) => {
    const normalizedChatId = String(chatId || "").trim();
    if (!normalizedChatId) return;
    setHideTargetChatId(normalizedChatId);
  };

  const closeHideConversationConfirm = () => {
    if (hideConfirmSubmitting) return;
    setHideTargetChatId("");
  };

  const confirmHideConversation = async () => {
    const chatId = String(hideTargetChatId || "").trim();
    if (!chatId || hideConfirmSubmitting) return;
    setHideConfirmSubmitting(true);
    try {
      await hideConversation(chatId);
      setHideTargetChatId("");
    } finally {
      setHideConfirmSubmitting(false);
    }
  };

  const activeChats = availableChats.filter((chat) => chat.status === "accepted");
  const incomingRequests = availableChats.filter(
    (chat) => chat.status === "pending" && !chat.isInitiatedByMe
  );
  const outgoingRequests = availableChats.filter(
    (chat) => chat.status === "pending" && chat.isInitiatedByMe
  );
  const sortConversationsByRecent = (a, b) =>
    String(b?.lastUpdatedAt || "").localeCompare(String(a?.lastUpdatedAt || ""))
  ;
  const pinnedIndexByChatId = new Map(
    pinnedConversationIds.map((chatPk, index) => [chatPk, index])
  );
  const sortConversationsForList = (a, b) => {
    const aChatPk = String(a?.chatPk || "").trim();
    const bChatPk = String(b?.chatPk || "").trim();
    const aPinnedIndex = pinnedIndexByChatId.has(aChatPk)
      ? pinnedIndexByChatId.get(aChatPk)
      : Number.POSITIVE_INFINITY;
    const bPinnedIndex = pinnedIndexByChatId.has(bChatPk)
      ? pinnedIndexByChatId.get(bChatPk)
      : Number.POSITIVE_INFINITY;

    if (aPinnedIndex !== bPinnedIndex) {
      return aPinnedIndex - bPinnedIndex;
    }
    return sortConversationsByRecent(a, b);
  };
  const chatRows = [...activeChats].sort(sortConversationsForList);
  const requestRows = [...incomingRequests, ...outgoingRequests].sort(sortConversationsByRecent);
  const visibleRows = activeListMode === "requests" ? requestRows : chatRows;
  const hasAnyConversations =
    activeChats.length > 0 || incomingRequests.length > 0 || outgoingRequests.length > 0;
  const hasAnyVisibleRows = visibleRows.length > 0;
  const requestCount = requestRows.length;
  const currentPage = activeListMode === "requests" ? requestsPage : chatsPage;
  const setCurrentPage = activeListMode === "requests" ? setRequestsPage : setChatsPage;
  const {
    safePage,
    totalPages,
    rangeStart,
    rangeEnd,
    totalItems: visibleRowsTotal,
  } = computePaginationRange({
    currentPage,
    pageSize: ACTIVE_LIST_PAGE_SIZE,
    totalItems: visibleRows.length,
  });
  const paginatedVisibleRows = visibleRows.slice(
    (safePage - 1) * ACTIVE_LIST_PAGE_SIZE,
    safePage * ACTIVE_LIST_PAGE_SIZE
  );
  const sectionListRef = useRef(null);

  useEffect(() => {
    if (totalPages <= 1) return;
    const node = sectionListRef.current;
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [safePage, activeListMode, totalPages]);

  useEffect(() => {
    if (chatsPage > 1 && (chatsPage - 1) * ACTIVE_LIST_PAGE_SIZE >= chatRows.length) {
      setChatsPage(Math.max(1, Math.ceil(chatRows.length / ACTIVE_LIST_PAGE_SIZE)));
    }
  }, [chatRows.length, chatsPage]);

  useEffect(() => {
    if (requestsPage > 1 && (requestsPage - 1) * ACTIVE_LIST_PAGE_SIZE >= requestRows.length) {
      setRequestsPage(Math.max(1, Math.ceil(requestRows.length / ACTIVE_LIST_PAGE_SIZE)));
    }
  }, [requestRows.length, requestsPage]);
  const toggleConversationPin = (chatPk) => {
    const normalizedChatPk = String(chatPk || "").trim();
    if (!normalizedChatPk) return;

    setPinnedConversationIds((previous) => {
      if (previous.includes(normalizedChatPk)) {
        return previous.filter((value) => value !== normalizedChatPk);
      }
      return [normalizedChatPk, ...previous];
    });
  };

  const blurIfPointerClick = (event) => {
    if (Number(event?.detail || 0) <= 0) return;
    if (typeof event?.currentTarget?.blur === "function") {
      event.currentTarget.blur();
    }
  };

  const renderConversationCard = (conversation, { showCardTools = true } = {}) => {
    const isIncoming = conversation.status === "pending" && !conversation.isInitiatedByMe;
    const isOutgoingPending = conversation.status === "pending" && conversation.isInitiatedByMe;
    const isRequestCard = isIncoming || isOutgoingPending;
    const isCardClickable = !isRequestCard || !showCardTools;
    const showIncomingRequestHoverActions = isIncoming && !showCardTools;
    const showSentRequestHoverCancel = isOutgoingPending && !showCardTools;
    const isOnline = conversation.presenceStatus === "online";
    const hasUnread = Number(conversation?.unreadCount || 0) > 0;
    const chatPk = String(conversation?.chatPk || "").trim();
    const isPinned = pinnedConversationIds.includes(chatPk);
    const isAccepting = acceptingChatRequestIds.includes(chatPk);
    const timestampLabel = formatConversationTimestamp(conversation?.lastUpdatedAt);
    const counterpartName = String(conversation.title || "this person").trim();
    const waitingMessageText = counterpartName
      ? `Waiting for ${counterpartName} to accept your conversation request.`
      : "Waiting for a response to your conversation request.";
    const requestTopic = String(
      conversation?.requestMessage || conversation?.previewText || ""
    ).trim();
    const statusText = isIncoming
      ? "Request pending"
      : isOutgoingPending
        ? "Awaiting response"
        : "";
    const statusPillClassName = `activeComms__statePill ${
      isIncoming
        ? "activeComms__statePill--incoming"
        : isOutgoingPending
          ? "activeComms__statePill--outgoing"
          : ""
    }`;
    const cardVariantClassName = isIncoming
      ? "activeComms__card--requestIn"
      : isOutgoingPending
        ? "activeComms__card--requestOut"
        : "";
    const previewText = isIncoming
      ? (requestTopic
          ? `Wants to discuss: ${requestTopic}`
          : `${counterpartName || "Someone"} sent you a conversation request.`)
      : isOutgoingPending
        ? (requestTopic ? `You requested: ${requestTopic}` : waitingMessageText)
        : (conversation.previewText?.trim() || "Open to view messages and respond…");
    const requestHintText = isIncoming
      ? "Accept to start messaging."
      : isOutgoingPending
        ? "Waiting for their response."
        : "";
    const showUnreadDot = isRequestCard ? false : hasUnread;
    const useUnreadTextTone = hasUnread;
    const presenceClassName = `activeComms__presenceDot ${
      isOnline ? "activeComms__presenceDot--online" : "activeComms__presenceDot--offline"
    }`;
    const openConversation = () => {
      goToChat(conversation.chatPk, conversation.title);
    };
    const handleCardKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openConversation();
      }
    };

    return (
      <div
        key={conversation.chatPk}
        className={`activeComms__card ${
          isIncoming ? "activeComms__card--pending" : ""
        } ${
          isOutgoingPending ? "activeComms__card--sent" : ""
        } ${
          hasUnread ? "activeComms__card--unread" : "activeComms__card--read"
        } ${
          isPinned ? "activeComms__card--pinned" : ""
        } ${
          cardVariantClassName
        } ${
          showCardTools ? "" : "activeComms__card--toolsDisabled"
        }`}
        onClick={isCardClickable ? openConversation : undefined}
        onKeyDown={isCardClickable ? handleCardKeyDown : undefined}
        role={isCardClickable ? "button" : undefined}
        tabIndex={isCardClickable ? 0 : undefined}
        aria-label={isCardClickable ? `Open chat with ${conversation.title || "user"}` : undefined}
      >
        <div className="activeComms__cardRow">
          <div className="activeComms__avatar">
            <button
              type="button"
              className="activeComms__avatarBtn"
              onClick={(event) => {
                event.stopPropagation();
                openProfileFromChat(conversation);
              }}
              aria-label={`View ${conversation.title} profile`}
            >
              <ProfileAvatar
                user={conversation}
                name={conversation.title}
                className="activeComms__avatarImg"
              />
            </button>
            {!conversation.isGroup && !isRequestCard ? (
              <span className={presenceClassName} aria-hidden="true" />
            ) : null}
          </div>

          <div className="activeComms__body">
            <div className="activeComms__topRow">
              <button
                type="button"
                className={`activeComms__nameBtn ${
                  useUnreadTextTone ? "activeComms__nameBtn--unread" : "activeComms__nameBtn--read"
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                  openProfileFromChat(conversation);
                }}
                title={conversation.title || undefined}
              >
                {conversation.title}
              </button>
              <div className={`activeComms__meta ${
                showCardTools ? "" : "activeComms__meta--staticOnly"
              } ${
                showIncomingRequestHoverActions ? "activeComms__meta--requestInHover" : ""
              } ${
                showSentRequestHoverCancel ? "activeComms__meta--requestOutHover" : ""
              }`}>
                <div className="activeComms__metaStatic">
                  {isPinned && !isRequestCard ? (
                    <span className="activeComms__pinnedHint" aria-label="Pinned conversation">
                      Pinned
                    </span>
                  ) : null}
                  {!isRequestCard && statusText ? (
                    <span className={statusPillClassName}>{statusText}</span>
                  ) : null}
                  {timestampLabel ? (
                    <span className="activeComms__time">{timestampLabel}</span>
                  ) : null}
                  {showUnreadDot ? (
                    <span className="activeComms__unreadDot" aria-label="Unread messages" />
                  ) : null}
                </div>
                {showIncomingRequestHoverActions ? (
                  <div className="activeComms__requestInMetaAction">
                    <button
                      type="button"
                      className="activeComms__requestInAcceptBtn"
                      onClick={async (event) => {
                        event.stopPropagation();
                        await acceptChatRequest(conversation.chatPk);
                        goToChat(conversation.chatPk, conversation.title);
                      }}
                      disabled={isAccepting}
                      aria-label={`Accept request from ${conversation.title || "user"}`}
                      aria-busy={isAccepting}
                    >
                      {isAccepting ? (
                        <>
                          <LoaderCircle size={14} className="activeComms__buttonSpinner" />
                          Accepting...
                        </>
                      ) : (
                        "Accept"
                      )}
                    </button>
                    <button
                      type="button"
                      className="activeComms__requestInIgnoreBtn"
                      onClick={(event) => {
                        event.stopPropagation();
                        denyChatRequest(conversation.chatPk);
                      }}
                      aria-label={`Ignore request from ${conversation.title || "user"}`}
                    >
                      Ignore
                    </button>
                  </div>
                ) : null}
                {showSentRequestHoverCancel ? (
                  <div className="activeComms__requestOutMetaAction">
                    <button
                      type="button"
                      className="activeComms__requestOutCancelBtn"
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteChatRequest(conversation.chatPk);
                      }}
                      aria-label={`Cancel request to ${conversation.title || "user"}`}
                    >
                      Cancel Request
                    </button>
                  </div>
                ) : null}
                {showCardTools ? (
                  <div className="activeComms__metaActions">
                    {!isIncoming && !isOutgoingPending ? (
                      <button
                        type="button"
                        className="activeComms__toolBtn activeComms__toolBtn--hide"
                        onClick={(event) => {
                          event.stopPropagation();
                          openHideConversationConfirm(conversation.chatPk);
                          blurIfPointerClick(event);
                        }}
                        aria-label={`Hide chat with ${conversation.title || "user"}`}
                      >
                        <EyeOff size={16} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className={`activeComms__pinBtn ${
                        isPinned ? "activeComms__pinBtn--active" : ""
                      }`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleConversationPin(conversation.chatPk);
                        blurIfPointerClick(event);
                      }}
                      aria-label={
                        isPinned
                          ? `Unpin chat with ${conversation.title || "user"}`
                          : `Pin chat with ${conversation.title || "user"}`
                      }
                      title={isPinned ? "Unpin conversation" : "Pin conversation"}
                    >
                      <Pin size={16} />
                    </button>
                    {!isIncoming && !isOutgoingPending ? (
                      <button
                        type="button"
                        className="activeComms__toolBtn activeComms__toolBtn--delete"
                        onClick={(event) => {
                          event.stopPropagation();
                          deleteChatRequest(conversation.chatPk);
                          blurIfPointerClick(event);
                        }}
                        aria-label={`Delete chat with ${conversation.title || "user"}`}
                        title="Delete conversation"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <div className={`activeComms__message ${
              useUnreadTextTone ? "activeComms__message--unread" : "activeComms__message--read"
            }`}>
              {previewText}
              {requestHintText ? (
                <div className="activeComms__requestHint">{requestHintText}</div>
              ) : null}
            </div>

            {isIncoming && !showIncomingRequestHoverActions ? (
              <div className="activeComms__actions">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={async (event) => {
                    event.stopPropagation();
                    await acceptChatRequest(conversation.chatPk);
                    goToChat(conversation.chatPk, conversation.title);
                  }}
                  className="activeComms__acceptBtn"
                  disabled={isAccepting}
                  aria-busy={isAccepting}
                >
                  {isAccepting ? (
                    <>
                      <LoaderCircle size={14} className="activeComms__buttonSpinner" />
                      Accepting...
                    </>
                  ) : (
                    "Accept"
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    denyChatRequest(conversation.chatPk);
                  }}
                  className="activeComms__denyBtn activeComms__denyBtn--request"
                >
                  Ignore
                </Button>
              </div>
            ) : isOutgoingPending && !showSentRequestHoverCancel ? (
              <div className="activeComms__actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    deleteChatRequest(conversation.chatPk);
                  }}
                  className="activeComms__denyBtn activeComms__denyBtn--request"
                >
                  Cancel Request
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="activeComms">
      <div className="activeComms__container">
        <div className="activeComms__grid">
          <div className="activeComms__main">
            <div className="activeComms__sections">
              <section className="activeComms__sectionBlock">
                <div className="activeComms__sectionHead">
                  <h2 className="activeComms__sectionTitle">
                    {activeListMode === "requests" ? "Requests" : "Chats"}
                  </h2>
                  <div className="activeComms__viewSwitch" role="tablist" aria-label="Conversation views">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeListMode === "chats"}
                      className={`activeComms__viewSwitchBtn ${
                        activeListMode === "chats" ? "activeComms__viewSwitchBtn--active" : ""
                      }`}
                      onClick={() => setActiveListMode("chats")}
                    >
                      Chats
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeListMode === "requests"}
                      className={`activeComms__viewSwitchBtn ${
                        activeListMode === "requests" ? "activeComms__viewSwitchBtn--active" : ""
                      } ${
                        requestCount > 0 ? "activeComms__viewSwitchBtn--hasBadge" : ""
                      }`}
                      onClick={() => setActiveListMode("requests")}
                    >
                      Requests
                      {requestCount > 0 ? (
                        <span className="activeComms__viewSwitchCount">{requestCount}</span>
                      ) : null}
                    </button>
                  </div>
                </div>

                {showInitialBlockingLoad ? (
                  <div
                    className="activeComms__subtleEmpty"
                    role="status"
                    aria-live="polite"
                  >
                    Loading communications…
                  </div>
                ) : !hasAnyVisibleRows ? (
                  <div
                    className="activeComms__subtleEmpty"
                    role="status"
                    aria-live="polite"
                  >
                    {chatListLoading
                      ? "Loading conversations…"
                      : activeListMode === "requests"
                        ? "No pending requests right now."
                        : hasAnyConversations
                          ? "No chats yet. Your accepted conversations will appear here."
                          : "No active communications yet. Browse the community and send a conversation request to get started."}
                  </div>
                ) : (
                  <>
                    <div className="activeComms__sectionList" ref={sectionListRef}>
                      {paginatedVisibleRows.map((conversation) =>
                        renderConversationCard(conversation, {
                          showCardTools: activeListMode === "chats",
                        })
                      )}
                    </div>
                    {totalPages > 1 ? (
                      <div
                        className="activeComms__pagination"
                        aria-label="Conversation list pagination"
                      >
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                          disabled={safePage === 1}
                        >
                          Previous
                        </Button>
                        <span
                          className="activeComms__paginationLabel"
                          aria-current="page"
                          aria-live="polite"
                        >
                          Page {safePage} of {totalPages}
                          <span className="activeComms__paginationRange">
                            {" "}· Showing {rangeStart}–{rangeEnd} of {visibleRowsTotal}
                          </span>
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                          disabled={safePage === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </section>
            </div>
          </div>

          {/* Ad placeholders intentionally disabled.
          <div className="activeComms__ads">
            <div className="activeComms__adCard activeComms__adCard--gray">
              <ShoppingBag className="activeComms__adIcon" />
              <div className="activeComms__adSale">SALE</div>
              <button className="activeComms__adLink" type="button">
                Shop Now <ArrowRight className="activeComms__adArrow" />
              </button>
            </div>

            <div className="activeComms__adCard activeComms__adCard--blue">
              <div className="activeComms__adHero" />
              <div className="activeComms__adFooter">
                <button className="activeComms__adLink" type="button">
                  Explore your style{" "}
                  <ArrowRight className="activeComms__adArrow" />
                </button>
              </div>
            </div>

            <div className="activeComms__adHint">(Ads are placeholders for now)</div>
          </div>
          */}
        </div>
      </div>

      {/* Profile and moderation dialogs */}
      {selectedProfile && (
        <ProfileDialog
          user={selectedProfile}
          onClose={closeProfile}
          calculateAge={calculateAge}
          isBlocked={selectedProfileIsBlocked}
          onToggleBlock={selectedProfileId ? toggleBlockForSelectedProfile : undefined}
          blockActionLoading={blockActionTargetId === selectedProfileId}
          onReportUser={selectedProfileId ? openReportForSelectedProfile : undefined}
        />
      )}

      <ReportDialog
        isOpen={reportDialogOpen}
        title="Report User"
        subtitle="Provide a reason so moderators can review this profile."
        targetLabel={reportTarget?.targetLabel || "User"}
        onClose={closeReportDialog}
        onSubmit={submitUserReport}
      />

      {/* Hide conversation confirmation */}
      <ConfirmActionDialog
        isOpen={Boolean(hideTargetChatId)}
        title="Hide Chat?"
        message="This chat will be hidden from your list (the other person isn't notified and can still message you). You can restore it from Settings → Hidden Chats."
        confirmLabel="Hide Chat"
        cancelLabel="Cancel"
        onClose={closeHideConversationConfirm}
        onConfirm={confirmHideConversation}
        submitting={hideConfirmSubmitting}
      />
    </div>
  );
}

export default function Active() {
  return <ActiveInner />;
}
